import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import { faComment } from '@fortawesome/pro-regular-svg-icons/faComment';
import { faPenToSquare } from '@fortawesome/pro-regular-svg-icons/faPenToSquare';
import { faSidebar } from '@fortawesome/pro-regular-svg-icons/faSidebar';
import { faSparkle } from '@fortawesome/pro-regular-svg-icons/faSparkle';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useLocationPathname, useNavigate, useParams } from '@rocketlane/rocket-router';
import { Button, Icon, Tooltip } from '@rocketlane/ui';
import { Fragment, useCallback, useEffect, useMemo, useState } from 'react';
import { NITRO_CLAW_AGENT_NAME } from 'api/nitro-claw';
import rocketlaneMark from 'assets/svgs/rocketlane_icon_standard.svg';
import { Popover } from 'components/atoms/popover';
import { useAppShellMode } from 'hooks/use-app-shell-mode';
import { useLocalStorage } from 'hooks/use-local-storage';
import { useIsRmAgentEnabled } from 'hooks/use-rm-agent';
import { useTranslate } from 'hooks/use-translation';
import { useAgentChatHistoryInfiniteQuery } from 'queries/ai-chat/ai-chat';
import { useRmAgentChatHistoryInfiniteQuery } from 'queries/ai-chat/rm-copilot';
import { useNitroBrewAgentsQuery } from 'queries/nitro-brew';
import { usePending } from 'stores/notifications';
import { useForReviewPending } from 'queries/nitro-review';
import { isTauriApp } from 'utils/is-tauri';
import nitroClawLogo from './assets/nitro-claw-logo.svg';
import { MeshSphere } from 'vendor/meshy';
import { buildPrimaryNavItems, pathEndsWith, RESOURCE_NAV_ITEMS, ROUTE_BASE, RESERVED_SEGMENTS, toNitroBrewSubItems } from './nitro-claw-desktop-sidebar.config';
import { useVisibleNitroClawAgentItems } from './use-visible-nitro-claw-agent-items';
import { NitroClawDesktopSidebarFooter } from './nitro-claw-desktop-sidebar-footer';
import { AgentChildNavRow, AgentNavMain, AgentNavRow, BrandBlock, BrandLogo, BrandTitle, ChatsEmpty, CollapseButton, CollapsedBrandHitArea, CollapsedFlyoutHeader, CollapsedFlyoutItem, CollapsedFlyoutPanel, CollapsedNavFlyoutOverlayGlobalStyle, COLLAPSED_NAV_FLYOUT_OVERLAY_CLASS, CountBadge, HeaderRow, NavIcon, NavLabel, NavRow, NavSection, RocketlaneBackMark, SectionLabel, ShowMoreButton, SidebarRoot, SidebarScroll } from './nitro-claw-desktop-sidebar.styles';
const INITIAL_VISIBLE_CHATS = 6;
const VISIBLE_CHATS_STEP = 6;
const MaybeTooltip = ({ tip, children })=>{
    if (!tip) {
        return /*#__PURE__*/ _jsx(_Fragment, {
            children: children
        });
    }
    return /*#__PURE__*/ _jsx(Tooltip, {
        content: tip,
        side: "right",
        children: children
    });
};
export function NitroClawDesktopSidebar() {
    const translate = useTranslate();
    const navigate = useNavigate();
    const pathname = useLocationPathname();
    const { conversationId } = useParams();
    const { switchToAppShell } = useAppShellMode();
    const [uiPreference, setUiPreference] = useLocalStorage('nitro-claw-desktop-sidebar', {
        collapsed: false
    });
    const collapsed = Boolean(uiPreference.collapsed);
    const [pending] = usePending();
    const forReviewPending = useForReviewPending();
    const showBackToRocketlane = !isTauriApp();
    const [visibleChatCount, setVisibleChatCount] = useState(INITIAL_VISIBLE_CHATS);
    const [hoveredAgentId, setHoveredAgentId] = useState(null);
    const [hoveredSubItemId, setHoveredSubItemId] = useState(null);
    const [expandedAgentIds, setExpandedAgentIds] = useState({});
    // Warm the lazy conversation-page chunk so opening a chat (from here, or from
    // an agent page's composer) doesn't flash the route-level Suspense spinner.
    useEffect(()=>{
        const prefetch = ()=>{
            import('components/pages/nitro-claw');
        };
        const idle = window.requestIdleCallback;
        if (idle) {
            const handle = idle(prefetch);
            return ()=>{
                var _window_cancelIdleCallback, _window;
                return (_window_cancelIdleCallback = (_window = window).cancelIdleCallback) === null || _window_cancelIdleCallback === void 0 ? void 0 : _window_cancelIdleCallback.call(_window, handle);
            };
        }
        const timer = window.setTimeout(prefetch, 1000);
        return ()=>window.clearTimeout(timer);
    }, []);
    const { data, isLoading } = useAgentChatHistoryInfiniteQuery({
        limit: 40
    }, NITRO_CLAW_AGENT_NAME);
    const isRmAgentEnabled = useIsRmAgentEnabled();
    const visibleAgentItems = useVisibleNitroClawAgentItems();
    const showNitroBrews = visibleAgentItems.some((item)=>item.id === 'nitro-brews');
    useRmAgentChatHistoryInfiniteQuery({
        limit: 40
    }, {
        enabled: isRmAgentEnabled
    });
    const { data: nitroBrewAgents = [] } = useNitroBrewAgentsQuery({
        enabled: !collapsed && showNitroBrews
    });
    const agentSubItemsById = useMemo(()=>{
        return {
            'nitro-brews': toNitroBrewSubItems(nitroBrewAgents)
        };
    }, [
        nitroBrewAgents
    ]);
    const conversations = useMemo(()=>{
        var _ref;
        const pages = (_ref = data === null || data === void 0 ? void 0 : data.pages) !== null && _ref !== void 0 ? _ref : [];
        return pages.flatMap((page)=>{
            var _page_conversations;
            return (_page_conversations = page.conversations) !== null && _page_conversations !== void 0 ? _page_conversations : [];
        }).slice(0, 20);
    }, [
        data === null || data === void 0 ? void 0 : data.pages
    ]);
    const visibleConversations = useMemo(()=>conversations.slice(0, visibleChatCount), [
        conversations,
        visibleChatCount
    ]);
    const hasMoreConversations = conversations.length > visibleChatCount;
    const showMoreConversations = useCallback(()=>{
        setVisibleChatCount((prev)=>Math.min(prev + VISIBLE_CHATS_STEP, conversations.length));
    }, [
        conversations.length
    ]);
    const activeConversationId = conversationId && !RESERVED_SEGMENTS.has(conversationId) && !pathEndsWith(pathname, 'for-review') ? conversationId : null;
    const primaryItems = useMemo(()=>buildPrimaryNavItems(pending, forReviewPending), [
        pending,
        forReviewPending
    ]);
    const toggleCollapsed = useCallback(()=>{
        setUiPreference((prev)=>({
                ...prev,
                collapsed: !prev.collapsed
            }));
    }, [
        setUiPreference
    ]);
    const go = useCallback((path)=>{
        navigate(path);
    }, [
        navigate
    ]);
    const handleAgentRowClick = useCallback((itemId, path, hasSubItems)=>{
        if (hasSubItems) {
            setExpandedAgentIds((prev)=>({
                    ...prev,
                    [itemId]: true
                }));
        }
        go(path);
    }, [
        go
    ]);
    const label = useCallback((id, defaultMessage)=>translate(id, undefined, defaultMessage), [
        translate
    ]);
    const collapseTooltip = collapsed ? label('nitro_claw.sidebar.expand', 'Expand sidebar') : label('nitro_claw.sidebar.collapse', 'Collapse sidebar');
    const isChatsSectionActive = pathname === ROUTE_BASE || pathname === `${ROUTE_BASE}/` || activeConversationId != null;
    const isAgentsSectionActive = visibleAgentItems.some((item)=>item.match(pathname));
    const showAgentsSection = visibleAgentItems.length > 0;
    const collapsedChatsFlyout = /*#__PURE__*/ _jsxs(CollapsedFlyoutPanel, {
        "data-cy": "nitro-claw-desktop-sidebar.chats-flyout",
        children: [
            /*#__PURE__*/ _jsx(CollapsedFlyoutHeader, {
                children: label('nitro_claw.sidebar.chats', 'Chats')
            }),
            /*#__PURE__*/ _jsxs(CollapsedFlyoutItem, {
                type: "button",
                $active: pathname === ROUTE_BASE || pathname === `${ROUTE_BASE}/`,
                onClick: ()=>go(ROUTE_BASE),
                "data-cy": "nitro-claw-desktop-sidebar.flyout-new-chat",
                children: [
                    /*#__PURE__*/ _jsx(NavIcon, {
                        children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                            icon: faPenToSquare
                        })
                    }),
                    /*#__PURE__*/ _jsx(NavLabel, {
                        children: label('nitro_claw.sidebar.new_chat', 'New chat')
                    })
                ]
            }),
            conversations.slice(0, INITIAL_VISIBLE_CHATS).map((conv)=>{
                var _conv_title;
                const title = ((_conv_title = conv.title) === null || _conv_title === void 0 ? void 0 : _conv_title.trim()) || label('nitro_claw.sidebar.untitled_chat', 'Untitled conversation');
                return /*#__PURE__*/ _jsxs(CollapsedFlyoutItem, {
                    type: "button",
                    $active: activeConversationId === conv.conversation_id,
                    onClick: ()=>go(`${ROUTE_BASE}/${conv.conversation_id}`),
                    "data-cy": `nitro-claw-desktop-sidebar.flyout-chat.${conv.conversation_id}`,
                    children: [
                        /*#__PURE__*/ _jsx(NavIcon, {
                            children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                icon: faComment
                            })
                        }),
                        /*#__PURE__*/ _jsx(NavLabel, {
                            title: title,
                            children: title
                        })
                    ]
                }, conv.conversation_id);
            }),
            !isLoading && conversations.length === 0 ? /*#__PURE__*/ _jsx(ChatsEmpty, {
                children: label('nitro_claw.sidebar.chats_empty', 'No conversations yet')
            }) : null
        ]
    });
    const collapsedAgentsFlyout = /*#__PURE__*/ _jsxs(CollapsedFlyoutPanel, {
        "data-cy": "nitro-claw-desktop-sidebar.agents-flyout",
        children: [
            /*#__PURE__*/ _jsx(CollapsedFlyoutHeader, {
                children: label('nitro_claw.sidebar.agents', 'Agents')
            }),
            visibleAgentItems.map((item)=>/*#__PURE__*/ _jsxs(CollapsedFlyoutItem, {
                    type: "button",
                    $active: item.match(pathname),
                    onClick: ()=>go(item.path),
                    onMouseEnter: ()=>setHoveredAgentId(item.id),
                    onMouseLeave: ()=>setHoveredAgentId(null),
                    "data-cy": `nitro-claw-desktop-sidebar.flyout-agent.${item.id}`,
                    children: [
                        /*#__PURE__*/ _jsx(MeshSphere, {
                            colors: item.mesh.colors,
                            meshId: item.mesh.meshId,
                            size: 16,
                            animated: hoveredAgentId === item.id,
                            "data-cy": `nitro-claw-desktop-sidebar.flyout-agent-icon.${item.id}`
                        }),
                        /*#__PURE__*/ _jsx(NavLabel, {
                            children: label(item.labelKey, item.defaultMessage)
                        })
                    ]
                }, item.id))
        ]
    });
    return /*#__PURE__*/ _jsxs(SidebarRoot, {
        $collapsed: collapsed,
        "data-cy": "nitro-claw-desktop-sidebar",
        "aria-label": label('nitro_claw.sidebar.title', 'Nitro Claw'),
        children: [
            /*#__PURE__*/ _jsx(HeaderRow, {
                $collapsed: collapsed,
                children: collapsed ? /*#__PURE__*/ _jsxs(CollapsedBrandHitArea, {
                    "data-cy": "nitro-claw-desktop-sidebar.collapsed-brand",
                    children: [
                        /*#__PURE__*/ _jsx(BrandLogo, {
                            src: nitroClawLogo,
                            alt: "",
                            width: 20,
                            height: 20,
                            "data-cy": "nitro-claw-desktop-sidebar.logo"
                        }),
                        /*#__PURE__*/ _jsx(Tooltip, {
                            content: collapseTooltip,
                            side: "right",
                            children: /*#__PURE__*/ _jsx(CollapseButton, {
                                type: "button",
                                onClick: toggleCollapsed,
                                "aria-label": collapseTooltip,
                                "data-cy": "nitro-claw-desktop-sidebar.collapse",
                                children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                    icon: faSidebar
                                })
                            })
                        })
                    ]
                }) : /*#__PURE__*/ _jsxs(_Fragment, {
                    children: [
                        /*#__PURE__*/ _jsxs(BrandBlock, {
                            children: [
                                /*#__PURE__*/ _jsx(BrandLogo, {
                                    src: nitroClawLogo,
                                    alt: "",
                                    width: 20,
                                    height: 20,
                                    "data-cy": "nitro-claw-desktop-sidebar.logo"
                                }),
                                /*#__PURE__*/ _jsx(BrandTitle, {
                                    children: label('nitro_claw.sidebar.title', 'Nitro Claw')
                                })
                            ]
                        }),
                        /*#__PURE__*/ _jsx(Tooltip, {
                            content: collapseTooltip,
                            side: "right",
                            children: /*#__PURE__*/ _jsx(CollapseButton, {
                                type: "button",
                                onClick: toggleCollapsed,
                                "aria-label": collapseTooltip,
                                "data-cy": "nitro-claw-desktop-sidebar.collapse",
                                children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                    icon: faSidebar
                                })
                            })
                        })
                    ]
                })
            }),
            /*#__PURE__*/ _jsxs(SidebarScroll, {
                children: [
                    /*#__PURE__*/ _jsxs(NavSection, {
                        children: [
                            showBackToRocketlane ? /*#__PURE__*/ _jsx(MaybeTooltip, {
                                tip: collapsed ? label('nitro_claw.sidebar.back_to_rocketlane', 'Back to Rocketlane') : undefined,
                                children: /*#__PURE__*/ _jsxs(NavRow, {
                                    type: "button",
                                    $active: false,
                                    onClick: ()=>switchToAppShell('rocketlane'),
                                    "data-cy": "nitro-claw-desktop-sidebar.back-to-rocketlane",
                                    children: [
                                        /*#__PURE__*/ _jsx(NavIcon, {
                                            children: /*#__PURE__*/ _jsx(RocketlaneBackMark, {
                                                src: rocketlaneMark,
                                                alt: "",
                                                "aria-hidden": true
                                            })
                                        }),
                                        !collapsed ? /*#__PURE__*/ _jsx(NavLabel, {
                                            children: label('nitro_claw.sidebar.back_to_rocketlane', 'Back to Rocketlane')
                                        }) : null
                                    ]
                                })
                            }) : null,
                            primaryItems.map((item)=>/*#__PURE__*/ _jsx(MaybeTooltip, {
                                    tip: collapsed ? label(item.labelKey, item.defaultMessage) : undefined,
                                    children: /*#__PURE__*/ _jsxs(NavRow, {
                                        type: "button",
                                        $active: item.match(pathname),
                                        onClick: ()=>go(item.path),
                                        "data-cy": `nitro-claw-desktop-sidebar.${item.id}`,
                                        children: [
                                            /*#__PURE__*/ _jsx(NavIcon, {
                                                children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                                    icon: item.icon
                                                })
                                            }),
                                            !collapsed ? /*#__PURE__*/ _jsxs(_Fragment, {
                                                children: [
                                                    /*#__PURE__*/ _jsx(NavLabel, {
                                                        children: label(item.labelKey, item.defaultMessage)
                                                    }),
                                                    item.badge != null && item.badge > 0 ? /*#__PURE__*/ _jsx(CountBadge, {
                                                        children: item.badge
                                                    }) : null
                                                ]
                                            }) : null
                                        ]
                                    })
                                }, item.id))
                        ]
                    }),
                    /*#__PURE__*/ _jsx(NavSection, {
                        $collapsed: collapsed,
                        children: collapsed ? /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(CollapsedNavFlyoutOverlayGlobalStyle, {}),
                                /*#__PURE__*/ _jsx(Popover, {
                                    placement: "rightTop",
                                    trigger: "hover",
                                    mouseEnterDelay: 0.05,
                                    mouseLeaveDelay: 0.15,
                                    getPopupContainer: ()=>document.body,
                                    className: COLLAPSED_NAV_FLYOUT_OVERLAY_CLASS,
                                    content: collapsedChatsFlyout,
                                    children: /*#__PURE__*/ _jsx(NavRow, {
                                        type: "button",
                                        $active: isChatsSectionActive,
                                        "aria-label": label('nitro_claw.sidebar.chats', 'Chats'),
                                        "data-cy": "nitro-claw-desktop-sidebar.chats-collapsed",
                                        children: /*#__PURE__*/ _jsx(NavIcon, {
                                            children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                                icon: faComment
                                            })
                                        })
                                    })
                                })
                            ]
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(SectionLabel, {
                                    "data-cy": "nitro-claw-desktop-sidebar.chats-label",
                                    children: label('nitro_claw.sidebar.chats', 'Chats')
                                }),
                                /*#__PURE__*/ _jsxs(NavRow, {
                                    type: "button",
                                    $active: pathname === ROUTE_BASE || pathname === `${ROUTE_BASE}/`,
                                    onClick: ()=>go(ROUTE_BASE),
                                    "data-cy": "nitro-claw-desktop-sidebar.new-chat",
                                    children: [
                                        /*#__PURE__*/ _jsx(NavIcon, {
                                            children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                                icon: faPenToSquare
                                            })
                                        }),
                                        /*#__PURE__*/ _jsx(NavLabel, {
                                            children: label('nitro_claw.sidebar.new_chat', 'New chat')
                                        })
                                    ]
                                }),
                                visibleConversations.map((conv)=>{
                                    var _conv_title;
                                    const title = ((_conv_title = conv.title) === null || _conv_title === void 0 ? void 0 : _conv_title.trim()) || label('nitro_claw.sidebar.untitled_chat', 'Untitled conversation');
                                    return /*#__PURE__*/ _jsxs(NavRow, {
                                        type: "button",
                                        $active: activeConversationId === conv.conversation_id,
                                        onClick: ()=>go(`${ROUTE_BASE}/${conv.conversation_id}`),
                                        "data-cy": `nitro-claw-desktop-sidebar.chat.${conv.conversation_id}`,
                                        children: [
                                            /*#__PURE__*/ _jsx(NavIcon, {
                                                children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                                    icon: faComment
                                                })
                                            }),
                                            /*#__PURE__*/ _jsx(NavLabel, {
                                                title: title,
                                                children: title
                                            })
                                        ]
                                    }, conv.conversation_id);
                                }),
                                hasMoreConversations ? /*#__PURE__*/ _jsx(ShowMoreButton, {
                                    type: "button",
                                    onClick: showMoreConversations,
                                    "data-cy": "nitro-claw-desktop-sidebar.chats-show-more",
                                    children: label('show_more', 'Show more')
                                }) : null,
                                !isLoading && conversations.length === 0 ? /*#__PURE__*/ _jsx(ChatsEmpty, {
                                    children: label('nitro_claw.sidebar.chats_empty', 'No conversations yet')
                                }) : null
                            ]
                        })
                    }),
                    showAgentsSection ? /*#__PURE__*/ _jsx(NavSection, {
                        $collapsed: collapsed,
                        children: collapsed ? /*#__PURE__*/ _jsx(Popover, {
                            placement: "rightTop",
                            trigger: "hover",
                            mouseEnterDelay: 0.05,
                            mouseLeaveDelay: 0.15,
                            getPopupContainer: ()=>document.body,
                            className: COLLAPSED_NAV_FLYOUT_OVERLAY_CLASS,
                            content: collapsedAgentsFlyout,
                            children: /*#__PURE__*/ _jsx(NavRow, {
                                type: "button",
                                $active: isAgentsSectionActive,
                                "aria-label": label('nitro_claw.sidebar.agents', 'Agents'),
                                "data-cy": "nitro-claw-desktop-sidebar.agents-collapsed",
                                children: /*#__PURE__*/ _jsx(NavIcon, {
                                    children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                        icon: faSparkle
                                    })
                                })
                            })
                        }) : /*#__PURE__*/ _jsxs(_Fragment, {
                            children: [
                                /*#__PURE__*/ _jsx(SectionLabel, {
                                    "data-cy": "nitro-claw-desktop-sidebar.agents-label",
                                    children: label('nitro_claw.sidebar.agents', 'Agents')
                                }),
                                visibleAgentItems.map((item)=>{
                                    var _item_actions, _agentSubItemsById_item_id, _expandedAgentIds_item_id;
                                    const rowActions = (_item_actions = item.actions) !== null && _item_actions !== void 0 ? _item_actions : [];
                                    const subItems = (_agentSubItemsById_item_id = agentSubItemsById[item.id]) !== null && _agentSubItemsById_item_id !== void 0 ? _agentSubItemsById_item_id : [];
                                    const hasSubItems = subItems.length > 0;
                                    const isSectionExpanded = (_expandedAgentIds_item_id = expandedAgentIds[item.id]) !== null && _expandedAgentIds_item_id !== void 0 ? _expandedAgentIds_item_id : hasSubItems;
                                    const visibleSubItems = isSectionExpanded ? subItems : [];
                                    return /*#__PURE__*/ _jsxs(Fragment, {
                                        children: [
                                            /*#__PURE__*/ _jsxs(AgentNavRow, {
                                                $active: item.match(pathname),
                                                onMouseEnter: ()=>setHoveredAgentId(item.id),
                                                onMouseLeave: ()=>setHoveredAgentId(null),
                                                children: [
                                                    /*#__PURE__*/ _jsxs(AgentNavMain, {
                                                        type: "button",
                                                        "aria-expanded": hasSubItems ? isSectionExpanded : undefined,
                                                        onClick: ()=>handleAgentRowClick(item.id, item.path, hasSubItems),
                                                        "data-cy": `nitro-claw-desktop-sidebar.agent.${item.id}`,
                                                        children: [
                                                            /*#__PURE__*/ _jsx(MeshSphere, {
                                                                colors: item.mesh.colors,
                                                                meshId: item.mesh.meshId,
                                                                size: 16,
                                                                animated: hoveredAgentId === item.id,
                                                                "data-cy": `nitro-claw-desktop-sidebar.agent-icon.${item.id}`
                                                            }),
                                                            /*#__PURE__*/ _jsx(NavLabel, {
                                                                children: label(item.labelKey, item.defaultMessage)
                                                            })
                                                        ]
                                                    }),
                                                    rowActions.map((action)=>{
                                                        const actionTip = label(action.labelKey, action.defaultMessage);
                                                        return /*#__PURE__*/ _jsx(Tooltip, {
                                                            content: actionTip,
                                                            side: "top",
                                                            children: /*#__PURE__*/ _jsx(Button, {
                                                                type: "button",
                                                                variant: "icon",
                                                                size: "small",
                                                                "aria-label": actionTip,
                                                                leftIcon: /*#__PURE__*/ _jsx(Icon, {
                                                                    icon: action.icon
                                                                }),
                                                                onClick: ()=>go(action.path),
                                                                "data-cy": `nitro-claw-desktop-sidebar.agent-action.${item.id}.${action.id}`
                                                            })
                                                        }, action.id);
                                                    })
                                                ]
                                            }),
                                            visibleSubItems.map((subItem)=>{
                                                const subActive = pathname === subItem.path || pathname.startsWith(`${subItem.path}/`);
                                                const hoverKey = `${item.id}:${subItem.id}`;
                                                return /*#__PURE__*/ _jsxs(AgentChildNavRow, {
                                                    type: "button",
                                                    $active: subActive,
                                                    title: subItem.label,
                                                    onClick: ()=>go(subItem.path),
                                                    onMouseEnter: ()=>setHoveredSubItemId(hoverKey),
                                                    onMouseLeave: ()=>setHoveredSubItemId(null),
                                                    "data-cy": `nitro-claw-desktop-sidebar.agent-sub.${item.id}.${subItem.id}`,
                                                    children: [
                                                        /*#__PURE__*/ _jsx(MeshSphere, {
                                                            colors: subItem.mesh.colors,
                                                            meshId: subItem.mesh.meshId,
                                                            size: 12,
                                                            animated: hoveredSubItemId === hoverKey,
                                                            "data-cy": `nitro-claw-desktop-sidebar.agent-sub-icon.${item.id}.${subItem.id}`
                                                        }),
                                                        /*#__PURE__*/ _jsx(NavLabel, {
                                                            children: subItem.label
                                                        })
                                                    ]
                                                }, subItem.id);
                                            })
                                        ]
                                    }, item.id);
                                })
                            ]
                        })
                    }) : null,
                    /*#__PURE__*/ _jsxs(NavSection, {
                        $collapsed: collapsed,
                        children: [
                            !collapsed ? /*#__PURE__*/ _jsx(SectionLabel, {
                                "data-cy": "nitro-claw-desktop-sidebar.resources-label",
                                children: label('nitro_claw.sidebar.resources', 'Resources')
                            }) : null,
                            RESOURCE_NAV_ITEMS.map((item)=>/*#__PURE__*/ _jsx(MaybeTooltip, {
                                    tip: collapsed ? label(item.labelKey, item.defaultMessage) : undefined,
                                    children: /*#__PURE__*/ _jsxs(NavRow, {
                                        type: "button",
                                        $active: item.match(pathname),
                                        onClick: ()=>go(item.path),
                                        "data-cy": `nitro-claw-desktop-sidebar.${item.id}`,
                                        children: [
                                            /*#__PURE__*/ _jsx(NavIcon, {
                                                children: /*#__PURE__*/ _jsx(FontAwesomeIcon, {
                                                    icon: item.icon
                                                })
                                            }),
                                            !collapsed ? /*#__PURE__*/ _jsx(NavLabel, {
                                                children: label(item.labelKey, item.defaultMessage)
                                            }) : null
                                        ]
                                    })
                                }, item.id))
                        ]
                    })
                ]
            }),
            /*#__PURE__*/ _jsx(NitroClawDesktopSidebarFooter, {
                collapsed: collapsed
            })
        ]
    });
}
